<script>
import { required } from 'vuelidate/lib/validators';
import { cloneDeep } from 'lodash';
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import MaskedInput from "vue-text-mask";
import DatePicker from "vue2-datepicker";

export default {
    name: "RcdModal",
    props: {
        selectedCollections: {
            type: Array,
            default: () => []
        },
    },
    components: {
        DatePicker,
        MaskedInput
    },
    validations: {
        form: {
            rcdNumber: { required },
            rcdDate: { required },
            description: { required },
            cashOnHand: { required },
            totalDeposit: { required },
            depositBankName: { required },
            depositDate: { required },
            depositReferenceNumber: { required },
            depositedBy: { required }
        }
    },
    data() {
        return {
            form: {
                id: 0,
                rcdNumber: '',
                rcdDate: new Date(),
                description: '',
                cashOnHand: 0,
                totalDeposit: 0,
                depositBankName: '',
                depositDate: new Date(),
                depositReferenceNumber: '',
                depositedBy: ''
            },
            currencyMask: createNumberMask({
                prefix: "",
                allowDecimal: true,
            }),
            currentPage: 1,
            perPage: 10,
            submitted: false,
            loadRCDNumber: false
        }
    },
    computed: {
        totalCollectionAmount() {
            if(this.form.id == 0){
                return this.selectedCollections.reduce((total, collection) => {
                    return total + this.getCollectionTotal(collection);
                }, 0);
            }else{
                return this.selectedCollections.reduce((total, detail) => {
                    return total + detail.totalAmount;
                }, 0);
            }
        },
        rows() {
            return this.selectedCollections.length;
        },
        filtered() {
            let data = this.selectedCollections;
            var total = this.currentPage * this.perPage;
            var currentData = data.slice(total - this.perPage, total);
            if (this.currentPage > 1 && currentData.length == 0) {
                total = (this.currentPage - 1) * this.perPage;
                currentData = data.slice(total - this.perPage, total);
            }
            return currentData;
        },
    },
    methods: {
        
        getCollectionTotal(collection) {
            return collection.collectionItems ? 
                collection.collectionItems.reduce((total, item) => total + item.amount, 0) : 0;
        },
        
        resetForm() {
            this.form = {
                id: 0,
                rcdNumber: '',
                rcdDate: new Date(),
                description: '',
                cashOnHand: '',
                totalDeposit: '',
                depositBankName: '',
                depositDate: new Date(),
                depositReferenceNumber: '',
                depositedBy: ''
            };
            this.submitted = false;
            this.$v.$reset();
        },
        updateData(data){
            this.form = JSON.parse(JSON.stringify(data));
            this.form.rcdDate = new Date(data.rcdDate);
            this.form.depositDate = new Date(data.depositDate);
            this.form.cashOnHand = data.cashOnHand.toString();
        },
        onCreate() {
            this.submitted = true;
            this.form.totalDeposit = this.totalCollectionAmount;
            this.$v.form.$touch();
            
            if (this.$v.form.$invalid) {
                this.showToast("Please fill in all required fields", "error");
                return;
            }
            let collections = this.selectedCollections.map(collection => collection.id);
            const data = {
                ...cloneDeep(this.form),
                rcdDate: this.getDate(this.form.rcdDate),
                depositDate: this.getDate(this.form.depositDate),
                cashOnHand: this.getExactAmt(this.form.cashOnHand),
                collectionIds: collections
            };
            
            this.$emit('submit', data);
        },
        
        onCancel() {
            this.$bvModal.hide('rcd-modal');
        }
    }
}
</script>

<template>
    <b-modal
        id="rcd-modal"
        title="Create Report of Collections & Deposits"
        size="xl"
        title-class="font-18"
        hide-footer
        no-close-on-backdrop
    >
        <form @submit.prevent="onCreate" class="needs-validation">
            <div class="row">
                <div class="col-md-6">
                    <div class="row">
                        <div class="col-md-6">
                            <div class="mb-3">
                                <label for="rcdNumber">RCD Number </label>
                                <template v-if="loadRCDNumber">
                                    <div class="d-flex justify-content-center align-items-center" style="height:38px;">
                                        <div class="spinner-border spinner-border-sm text-primary" role="status">
                                            <span class="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                </template>
                                <template v-else>
                                    <b-form-input
                                        id="rcdNumber"
                                        v-model="form.rcdNumber"
                                        type="text"
                                        placeholder="Enter RCD Number..."
                                        autocomplete="off"
                                        :class="{'is-invalid': submitted && $v.form.rcdNumber.$error}"
                                    />
                                </template>
                                <div
                                    v-if="submitted && $v.form.rcdNumber.$error"
                                    class="invalid-feedback"
                                >
                                    <span v-if="!$v.form.rcdNumber.required">This field is required.</span>
                                </div>
                            </div>
                        </div>
                        
                        <div class="col-md-6">
                            <div class="mb-3">
                                <label for="rcdDate">RCD Date </label>
                                <date-picker
                                    id="rcdDate"
                                    v-model="form.rcdDate"
                                    :first-day-of-week="1"
                                    lang="en"
                                    placeholder="Enter RCD Date..."
                                    format="MMMM DD, YYYY"
                                    :class="{
                                        'is-invalid': submitted && $v.form.rcdDate.$error,
                                    }"
                                />
                                <div
                                    v-if="submitted && $v.form.rcdDate.$error"
                                    class="invalid-feedback"
                                >
                                    <span v-if="!$v.form.rcdDate.required">This field is required.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="mb-3">
                        <label for="description">Description </label>
                        <b-form-textarea
                            id="description"
                            v-model="form.description"
                            placeholder="Enter Description..."
                            rows="3"
                            autocomplete="off"
                            :class="{
                                'is-invalid': submitted && $v.form.description.$error,
                            }"
                        />
                        <div
                            v-if="submitted && $v.form.description.$error"
                            class="invalid-feedback"
                        >
                            <span v-if="!$v.form.description.required">This field is required.</span>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="mb-3">
                                <label for="cashOnHand">Cash on Hand </label>
                                <masked-input
                                    autocomplete="off"
                                    type="text"
                                    v-model="form.cashOnHand"
                                    placeholder="Enter Cash on Hand..."
                                    class="form-control"
                                    :class="{
                                    'is-invalid': submitted && $v.form.cashOnHand.$error,
                                    }"
                                    :mask="currencyMask"
                                ></masked-input>
                                <div
                                    v-if="submitted && $v.form.cashOnHand.$error"
                                    class="invalid-feedback"
                                >
                                    <span v-if="!$v.form.cashOnHand.required">This field is required.</span>
                                </div>
                            </div>
                        </div>
                        
                        <div class="col-md-6">
                            <div class="mb-3">
                                <label for="totalDeposit">Total Deposit </label>
                                <div class="form-control">
                                    <span class="text-orange font-weight-bold">{{formatCurrency(totalCollectionAmount)}}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="mb-3">
                                <label for="depositBankName">Deposit Bank Name </label>
                                <b-form-input
                                    id="depositBankName"
                                    v-model="form.depositBankName"
                                    type="text"
                                    placeholder="Enter Bank Name..."
                                    autocomplete="off"
                                    :class="{
                                        'is-invalid': submitted && $v.form.depositBankName.$error,
                                    }"
                                />
                                <div
                                    v-if="submitted && $v.form.depositBankName.$error"
                                    class="invalid-feedback"
                                >
                                    <span v-if="!$v.form.depositBankName.required">This field is required.</span>
                                </div>
                            </div>
                        </div>
                        
                        <div class="col-md-6">
                            <div class="mb-3">
                                <label for="depositDate">Deposit Date </label>
                                <date-picker
                                    id="depositDate"
                                    v-model="form.depositDate"
                                    :first-day-of-week="1"
                                    lang="en"
                                    placeholder="Enter Deposit Date..."
                                    format="MMMM DD, YYYY"
                                    :class="{
                                        'is-invalid': submitted && $v.form.depositDate.$error,
                                    }"
                                />
                                <div
                                    v-if="submitted && $v.form.depositDate.$error"
                                    class="invalid-feedback"
                                >
                                    <span v-if="!$v.form.depositDate.required">This field is required.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6">
                            <div class="mb-3">
                                <label for="depositReferenceNumber">Deposit Reference Number </label>
                                <b-form-input
                                    id="depositReferenceNumber"
                                    v-model="form.depositReferenceNumber"
                                    type="text"
                                    placeholder="Enter Reference Number..."
                                    autocomplete="off"
                                    :class="{
                                        'is-invalid': submitted && $v.form.depositReferenceNumber.$error,
                                    }"
                                />
                                <div
                                    v-if="submitted && $v.form.depositReferenceNumber.$error"
                                    class="invalid-feedback"
                                >
                                    <span v-if="!$v.form.depositReferenceNumber.required">This field is required.</span>
                                </div>
                            </div>
                        </div>
                        
                        <div class="col-md-6">
                            <div class="mb-3">
                                <label for="depositedBy">Deposited By </label>
                                <b-form-input
                                    id="depositedBy"
                                    v-model="form.depositedBy"
                                    type="text"
                                    placeholder="Enter Depositor Name..."
                                    autocomplete="off"
                                    :class="{
                                        'is-invalid': submitted && $v.form.depositedBy.$error,
                                    }"
                                />
                                <div
                                    v-if="submitted && $v.form.depositedBy.$error"
                                    class="invalid-feedback"
                                >
                                    <span v-if="!$v.form.depositedBy.required">This field is required.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-md-6">
                    <div class="mb-3">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                        <h6 class="mb-0">Selected Collections Summary</h6>
                            <div
                                class="dataTables_paginate paging_simple_numbers float-end"
                            >
                                <ul class="pagination pagination-sm pagination-rounded mb-0">
                                    <!-- pagination -->
                                    <b-pagination
                                        v-model="currentPage"
                                        :total-rows="rows"
                                        :per-page="perPage"
                                    ></b-pagination>
                                </ul>
                            </div>
                        </div>
                        <div class="table-responsive">
                            <table class="table table-sm table-bordered">
                                <thead class="table-light">
                                    <tr>
                                        <th>OR #</th>
                                        <th>Particular</th>
                                        <th class="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="collection in filtered" :key="collection.id">
                                        <td>{{ collection.orNumber }}</td>
                                        <td>{{ collection.particular }}</td>
                                        <td class="text-end">{{ form.id == 0 ? formatCurrency(getCollectionTotal(collection)) : formatCurrency(collection.totalAmount) }}</td>
                                    </tr>
                                </tbody>
                                <tfoot class="table-light">
                                    <tr>
                                        <td colspan="2" class="text-end"><strong>Total:</strong></td>
                                        <td class="text-end"><strong>{{ formatCurrency(totalCollectionAmount)  }}</strong></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Modal Footer -->
            <hr />
            <div class="text-end">
                <button type="submit" class="btn btn-primary">
                    <i class="bx bx-save me-1"></i>
                    {{form.id == 0 ? 'Create' : 'Update'}} RCD
                </button>
                <button
                    type="button"
                    class="btn btn-link ms-2"
                    @click="onCancel"
                >
                    Cancel
                </button>
            </div>
        </form>
    </b-modal>
</template>

<style scoped>
.table th, .table td {
    vertical-align: middle;
}

.is-invalid .mx-datepicker {
    border: 1px solid #f46a6a;
}

.spinner-border-sm {
    width: 1rem;
    height: 1rem;
}
</style>